<template>
  <SheetContent>
    <SheetHeader padded class="!p-2 !px-4 sm:!p-4 sm:!px-10">
      <SheetTitle size="lg" class="!text-base sm:!text-2xl">Send Email to {{ props.contactName }}</SheetTitle>
    </SheetHeader>

    <Separator />

    <SheetBody padded>
      <EmailInput
        v-model:subject="email.subject"
        v-model:body="email.body"
        v-model:files="files"
        v-model:invoke-on="invokeOn"
        :is-scheduled="isScheduled"
        :show-file-attachment="true"
        :delivery-mode="'user'"
      />
      <div v-if="isScheduled" class="flex items-center justify-between gap-4">
        <Button class="w-1/2 mt-3 sm:mt-6" @click="isScheduled = false"> Go back </Button>
        <Button class="w-1/2 mt-3 sm:mt-6" :disabled="!email.subject || !hasBody || !invokeOn" @click="scheduleEmail">
          Schedule
          <LocalLoadingSpinner />
        </Button>
      </div>
      <div v-else class="flex items-center justify-between gap-4">
        <Button class="w-1/2 mt-3 sm:mt-6" :disabled="!email.subject || !hasBody" @click="sendEmail">
          Send
          <LocalLoadingSpinner />
        </Button>
        <Button class="w-1/2 mt-3 sm:mt-6" :disabled="!postmarkVerified" @click="isScheduled = true">
          Schedule for later
        </Button>
      </div>
    </SheetBody>
  </SheetContent>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { SheetContent, SheetHeader, SheetTitle, SheetBody } from '@/components/ui/sheet';
import EmailInput from '../EmailInput.vue';
import { toast } from 'vue-sonner';
import { useLoadingSpinnerStore } from '@/stores/loadingSpinner';
import { mergeTagQuillParser } from '../merge_tag/quill_input/mergeTagQuillParser';
import { Api } from '@/api';
import { useCurrentUserStore } from '@/stores/currentUser';

export type EmailSheetContentProps = {
  contactName: string;
  contactId: number;
  contactEmail: string;
};

const props = defineProps<EmailSheetContentProps>();
const emit = defineEmits<{
  reload: [];
}>();

const loadingSpinnerStore = useLoadingSpinnerStore();
const userStore = useCurrentUserStore();
const postmarkVerified = userStore.currentUser?.postmarkEmailVerified ?? false;

const hasBody = computed(() => !!email.value.body.replace(/<[^>]*>/g, '').trim());

const email = ref({
  subject: '',
  body: '',
});
const files = ref<FileList | null>(null);
const isScheduled = ref<boolean>(false);
const invokeOn = ref<string>('');

const sendEmail = async (): Promise<void> => {
  loadingSpinnerStore.showLocalLoadingSpinner();

  try {
    const response = await Api.Contacts.sendContactEmail({
      contactId: props.contactId,
      email: props.contactEmail,
      subject: email.value.subject,
      body: mergeTagQuillParser().serialize(email.value.body),
      files: files.value,
    });

    loadingSpinnerStore.hideLocalLoadingSpinner();

    if (response.status === 'success') {
      email.value = { subject: '', body: '' };
      files.value = null;
      emit('reload');
      toast.success('Email sent', {
        description: 'Your email was successfully sent to this contact.',
      });
    } else {
      toast.error('Email sending error', {
        description: response.messages.join(', '),
      });
    }
  } catch {
    loadingSpinnerStore.hideLocalLoadingSpinner();
    toast.error('Email sending error', {
      description: 'An unexpected error occurred.',
    });
  }
};

const scheduleEmail = async (): Promise<void> => {
  loadingSpinnerStore.showLocalLoadingSpinner();

  try {
    const response = await Api.ScheduledActions.createScheduledEmail({
      contactId: props.contactId,
      invokeOn: invokeOn.value,
      subject: email.value.subject,
      body: mergeTagQuillParser().serialize(email.value.body),
      files: files.value,
    });

    loadingSpinnerStore.hideLocalLoadingSpinner();

    if (response.status === 'success') {
      email.value = { subject: '', body: '' };
      files.value = null;
      invokeOn.value = '';
      isScheduled.value = false;
      emit('reload');
      toast.success('Email scheduled', {
        description: 'Your email was successfully scheduled for this contact.',
      });
    } else {
      toast.error('Email scheduling error', {
        description: response.messages.join(', '),
      });
    }
  } catch {
    loadingSpinnerStore.hideLocalLoadingSpinner();
    toast.error('Email scheduling error', {
      description: 'An unexpected error occurred.',
    });
  }
};
</script>
